/**
 * How the surface is found (spec docs/specs/2026-10-08-kiosk-web-split-design.md §3). Pure:
 * callers pass the build flag, the URL search string, the server answer and whether the
 * dev/preview override is allowed (src/config/surfaceEnv.ts). SurfaceContext does the effects.
 *
 * Precedence: `?surface=` (dev/preview builds only) → kiosk when EITHER the server's answer
 * (`/api/kiosk/info` `type`, live or its cached copy) or the build flag (`VITE_DOMAIN_TYPE`) says
 * kiosk (the safer side: kiosk-forbidden capabilities off, kiosk behaviours on) → the server's
 * answer when it is kiosk or mobile → the build flag → unknown.
 */
import type { KnownSurface, Surface } from './surfacePolicy';

/** `?surface=kiosk|mobile`: the dev/preview override, for the demo and screenshots. */
export const SURFACE_PARAM = 'surface';

/** What decided the surface: the dev/preview override, the server (or its cache), the build flag, or nothing yet. */
export type SurfaceSource = 'override' | 'server' | 'flag' | 'none';

export interface SurfaceResolution {
  surface: Surface;
  source: SurfaceSource;
}

/** `kiosk` or `mobile` (trimmed, any case); anything else (admin, empty, missing) is null. */
export function toKnownSurface(value: unknown): KnownSurface | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  return v === 'kiosk' || v === 'mobile' ? v : null;
}

/** The page-load `?surface=`, only where the override is allowed (DEV / VITE_UI_PREVIEW). */
export function readSurfaceOverride(search: string, allowed: boolean): KnownSurface | null {
  if (!allowed) return null;
  return toKnownSurface(new URLSearchParams(search).get(SURFACE_PARAM));
}

export interface ResolveSurfaceInput {
  override: KnownSurface | null;
  /** `/api/kiosk/info` type (or its cached copy); null/undefined when there is no answer. */
  server: string | null | undefined;
  /** VITE_DOMAIN_TYPE (or the dev VITE_DEV_DOMAIN_TYPE). */
  flag: string | undefined;
}

export function resolveSurface({ override, server, flag }: ResolveSurfaceInput): SurfaceResolution {
  if (override) return { surface: override, source: 'override' };
  const fromServer = toKnownSurface(server);
  const fromFlag = toKnownSurface(flag);
  // Either one saying kiosk makes it a kiosk; the source names which one did (the server first).
  if (fromServer === 'kiosk') return { surface: 'kiosk', source: 'server' };
  if (fromFlag === 'kiosk') return { surface: 'kiosk', source: 'flag' };
  if (fromServer) return { surface: fromServer, source: 'server' };
  if (fromFlag) return { surface: fromFlag, source: 'flag' };
  return { surface: 'unknown', source: 'none' };
}

/**
 * The warning when the server answers kiosk/mobile and differs from the build flag; else null.
 * The two can only differ as kiosk vs mobile, and kiosk wins either way (resolveSurface).
 */
export function surfaceDisagreement(flag: string | undefined, server: string | null | undefined): string | null {
  const f = toKnownSurface(flag);
  const s = toKnownSurface(server);
  if (!f || !s || f === s) return null;
  return `[surface] VITE_DOMAIN_TYPE=${f} but /api/kiosk/info says ${s}; using kiosk (kiosk wins when either says kiosk).`;
}
